import * as XLSX from 'xlsx';
import type { Movement } from '../types/@types/cash';

/**
 * Formatea una fecha ISO a cadena legible en zona horaria local
 */
function formatDateTime(isoString?: string | null): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return isoString;
  }
}

function formatDateOnly(isoString?: string | null): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return isoString;
  }
}

/**
 * Calcula anchos de columnas automáticos para que no aparezcan cortadas en Excel
 */
function autoFitColumns(rows: Record<string, any>[]): { wch: number }[] {
  if (!rows || rows.length === 0) return [];
  const keys = Object.keys(rows[0]);
  return keys.map((key) => {
    let maxLen = key.length;
    for (const r of rows) {
      const val = r[key] != null ? String(r[key]) : '';
      if (val.length > maxLen) {
        maxLen = Math.min(val.length, 50); // máx 50 caracteres de ancho
      }
    }
    return { wch: Math.max(maxLen + 3, 12) };
  });
}

/**
 * Exporta el historial de arqueos y sesiones de caja a un archivo Excel (.xlsx)
 */
export function exportSessionsToExcel(sessions: any[], filename?: string): void {
  const rows = sessions.map((s) => {
    const diff = s.difference != null ? Number(s.difference) : null;
    let arqueoStatus = '—';
    if (diff !== null) {
      if (Math.abs(diff) < 1000) arqueoStatus = 'Cuadrado';
      else if (diff > 0) arqueoStatus = 'Sobrante';
      else arqueoStatus = 'Faltante';
    }

    // Desglose de pagos
    const breakdown: Record<string, number> = {};
    if (Array.isArray(s.paymentBreakdown)) {
      for (const p of s.paymentBreakdown) {
        const method = p.method ? String(p.method).toUpperCase() : 'OTRO';
        breakdown[method] = (breakdown[method] || 0) + Number(p.amount || 0);
      }
    }

    return {
      'ID Sesión': s.id,
      'Estado': s.status === 'open' ? 'ABIERTA' : 'CERRADA',
      'Cajero / Usuario': s.openedBy || 'Desconocido',
      'Fecha Apertura': formatDateTime(s.openedAt),
      'Fecha Cierre': formatDateTime(s.closedAt),
      'Base Inicial ($)': Number(s.initialAmount || 0),
      'Ventas Totales ($)': Number(s.totalSales || 0),
      'Efectivo Contado ($)': s.finalAmount != null ? Number(s.finalAmount) : '',
      'Diferencia ($)': diff !== null ? diff : '',
      'Resultado Arqueo': arqueoStatus,
      'Efectivo ($)': breakdown['EFECTIVO'] || 0,
      'Datáfono / Tarjeta ($)': breakdown['TARJETA'] || breakdown['DATAFONO'] || 0,
      'Transferencia ($)': breakdown['TRANSFERENCIA'] || 0,
      'Observaciones': s.observations || '',
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = autoFitColumns(rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Historial de Arqueos');

  const name = filename || `Arqueos_Caja_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * Exporta el listado de movimientos de caja a un archivo Excel (.xlsx)
 */
export function exportMovementsToExcel(movements: Movement[], filename?: string): void {
  const TYPE_NAMES: Record<string, string> = {
    sale: 'Venta',
    withdraw: 'Retiro',
    expense: 'Gasto',
  };

  const rows = movements.map((m) => {
    const isIncome = m.type === 'sale';
    const amountVal = Number(m.amount || 0);

    return {
      'ID Movimiento': m.id,
      'Fecha': formatDateOnly(m.createdAt),
      'Hora': m.createdAt ? new Date(m.createdAt).toLocaleTimeString('es-CO') : '—',
      'Tipo de Movimiento': TYPE_NAMES[m.type] || m.type,
      'Monto ($)': amountVal,
      'Signo (+ / -)': isIncome ? '+' : '-',
      'Método de Pago': m.method ? m.method.toUpperCase() : 'EFECTIVO',
      'Motivo / Concepto': m.reason || (isIncome ? 'Venta registrada' : 'Movimiento de caja'),
      'ID Pedido': m.orderId || (m.order?.id ? String(m.order.id) : '—'),
      'Destino Pedido': m.order?.destination === 'TAKEAWAY' ? 'Para Llevar' : m.order?.destination === 'MESA' ? 'Mesa' : '—',
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = autoFitColumns(rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Movimientos de Caja');

  const name = filename || `Movimientos_Caja_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * Exporta un Libro Completo de Caja con 2 hojas: Arqueos y Movimientos
 */
export function exportFullCashReportToExcel(
  sessions: any[],
  movements: Movement[],
  filename?: string
): void {
  const wb = XLSX.utils.book_new();

  // Hoja 1: Arqueos
  if (sessions.length > 0) {
    const sessionRows = sessions.map((s) => {
      const diff = s.difference != null ? Number(s.difference) : null;
      let arqueoStatus = '—';
      if (diff !== null) {
        if (Math.abs(diff) < 1000) arqueoStatus = 'Cuadrado';
        else if (diff > 0) arqueoStatus = 'Sobrante';
        else arqueoStatus = 'Faltante';
      }

      const breakdown: Record<string, number> = {};
      if (Array.isArray(s.paymentBreakdown)) {
        for (const p of s.paymentBreakdown) {
          const method = p.method ? String(p.method).toUpperCase() : 'OTRO';
          breakdown[method] = (breakdown[method] || 0) + Number(p.amount || 0);
        }
      }

      return {
        'ID Sesión': s.id,
        'Estado': s.status === 'open' ? 'ABIERTA' : 'CERRADA',
        'Cajero / Usuario': s.openedBy || 'Desconocido',
        'Fecha Apertura': formatDateTime(s.openedAt),
        'Fecha Cierre': formatDateTime(s.closedAt),
        'Base Inicial ($)': Number(s.initialAmount || 0),
        'Ventas Totales ($)': Number(s.totalSales || 0),
        'Efectivo Contado ($)': s.finalAmount != null ? Number(s.finalAmount) : '',
        'Diferencia ($)': diff !== null ? diff : '',
        'Resultado Arqueo': arqueoStatus,
        'Efectivo ($)': breakdown['EFECTIVO'] || 0,
        'Datáfono / Tarjeta ($)': breakdown['TARJETA'] || breakdown['DATAFONO'] || 0,
        'Transferencia ($)': breakdown['TRANSFERENCIA'] || 0,
        'Observaciones': s.observations || '',
      };
    });

    const wsSessions = XLSX.utils.json_to_sheet(sessionRows);
    wsSessions['!cols'] = autoFitColumns(sessionRows);
    XLSX.utils.book_append_sheet(wb, wsSessions, 'Historial de Arqueos');
  }

  // Hoja 2: Movimientos
  if (movements.length > 0) {
    const TYPE_NAMES: Record<string, string> = {
      sale: 'Venta',
      withdraw: 'Retiro',
      expense: 'Gasto',
    };

    const movRows = movements.map((m) => {
      const isIncome = m.type === 'sale';
      return {
        'ID Movimiento': m.id,
        'Fecha': formatDateOnly(m.createdAt),
        'Hora': m.createdAt ? new Date(m.createdAt).toLocaleTimeString('es-CO') : '—',
        'Tipo': TYPE_NAMES[m.type] || m.type,
        'Monto ($)': Number(m.amount || 0),
        'Signo': isIncome ? '+' : '-',
        'Método de Pago': m.method ? m.method.toUpperCase() : 'EFECTIVO',
        'Motivo / Concepto': m.reason || (isIncome ? 'Venta registrada' : 'Movimiento'),
        'ID Pedido': m.orderId || (m.order?.id ? String(m.order.id) : '—'),
        'Destino': m.order?.destination === 'TAKEAWAY' ? 'Para Llevar' : m.order?.destination === 'MESA' ? 'Mesa' : '—',
      };
    });

    const wsMov = XLSX.utils.json_to_sheet(movRows);
    wsMov['!cols'] = autoFitColumns(movRows);
    XLSX.utils.book_append_sheet(wb, wsMov, 'Movimientos Detallados');
  }

  const name = filename || `Reporte_Caja_Completo_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * Exporta reportes de ventas (órdenes y productos) a Excel (.xlsx)
 */
export function exportSalesToExcel(salesData: any[], filename?: string): void {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(salesData);
  ws['!cols'] = autoFitColumns(salesData);
  XLSX.utils.book_append_sheet(wb, ws, 'Reporte de Ventas');

  const name = filename || `Reporte_Ventas_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, name);
}
